import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Spinner } from "@radix-ui/themes";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DateFieldComponent from "../../../components/inputs/Datefield/DatefieldComponent";
import RadioGroupComponent from "../../../components/inputs/RadioGroup/RadioGroupComponent";
import TextFieldComponent from "../../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../../hooks/hooks";
import { resetUserData, setUserData } from "../../../redux/slices/users/user";
import { RootState } from "../../../redux/store";
import { routes } from "../../../routes";
import {
  createUser,
  getUserNameSuggestion,
} from "../../../services/user/createUser";
import { updateUser } from "../../../services/user/updateUser";
import { UserType } from "../../../types/User";
import { Create_Edit_UserFormValidation } from "./Create_Edit_UserFormValidation";
import { useCallback, useState } from "react";
import { debounce, isEmpty } from "lodash";
import { resetPassword } from "../../../services/user/resetPassword";
import { setPdfPreviewType } from "../../../redux/slices/report/report";
import { useGetRolesQuery } from "../../../hooks/useGetRolesQuery";
import { addUserRole } from "../../../services/user/addUserRole";
import { removeUserRole } from "../../../services/user/removeUserRole";
import { stringToBoolean } from "../../../utils/utilities";
import { Countries, Country } from "../../../utils/codes";
import SelectSearchableComponent from "../../../components/inputs/Select/SelectSearchableComponent";
import MultiSelectComponent from "../../../components/inputs/Select/MultiSelectComponent";
import * as yup from "yup";
import { AlertDialog } from '@radix-ui/themes';

export const Create_Edit_User = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isUpdateRoute = location.pathname === routes.updateUser;

  const dispatch = useAppDispatch();
  const defaultValues = useSelector((state: RootState) => state.user);
  const { roles: userRoles, claims: userClaims } = defaultValues;
  const [showChangesNotification, setShowChangesNotification] = useState(false);

  const mutation = useMutation({
    mutationFn: defaultValues.id && isUpdateRoute ? updateUser : createUser,
    mutationKey: "create_edit_User",
  });


  const {
    data: roles,
    isLoading: isLoadingRoles,
    isError: isErrorRoles,
  } = useGetRolesQuery({ PageNumber: 1, PageSize: 400 });
  const {
    mutateAsync: mutationRemoveUserRole,
    isLoading: isLoadingMutationRemoveUserRole,
  } = useMutation({
    mutationKey: "removeUserRole",
    mutationFn: removeUserRole,
  });
  const {
    mutateAsync: mutationAddUserRole,
    isLoading: isLoadingMutationAddUserRole,
  } = useMutation({ mutationKey: "addUserRole", mutationFn: addUserRole });

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    watch,
    reset,
    resetField,
  } = useForm<UserType>({
    resolver: yupResolver(Create_Edit_UserFormValidation),
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      data.twoFactorEnabled = stringToBoolean(data.twoFactorEnabled);
      data.isForeign = stringToBoolean(data.isForeign);
      const result = await mutation.mutateAsync(data);
      if (!result) throw "Erro a realizar esta ação";
      try {
        const userId =
          JSON.parse(JSON.stringify(result)).id! ?? defaultValues?.id;
        if (!isEmpty(defaultValues.id)) {
          let rolesToDelete = defaultValues.roleIds;
          if (rolesToDelete && rolesToDelete.length > 0) {
            for (let index = 0; index < rolesToDelete.length; index++) {
              const ri = rolesToDelete[index];
              await mutationRemoveUserRole({
                roleId: ri,
                userId: userId,
              });
            }
          }
        }
        if (data?.roleIds && data?.roleIds.length > 0) {
          for (let index = 0; index < data?.roleIds.length; index++) {
            const ri = data?.roleIds[index];
            await mutationAddUserRole({
              roleId: ri,
              userId: userId,
            });
          }
        }
        toast.success(
          `Utilizador ${defaultValues.id && isUpdateRoute ? "atualizado" : "registado"
          }.`
        );
        setShowChangesNotification(true);

      } catch (error) {
        console.log({ error });
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    dispatch(resetUserData());
    navigate(routes.users);
  };

  const handleResetPassword = async (user: UserType) => {
    try {
      const result = await resetPassword({ userId: user.id ?? "" });
      if (result) {
        toast.success("Senha de utilizador redefinida");
        user.password = result;
        user.icon = null;
        dispatch(setUserData(user));
        dispatch(setPdfPreviewType("RESET_PASSWORD"));
        navigate(routes.pdfPreview);
      }
    } catch (err: any) {
      toast.error("Erro ao criar utilizador.");
    }
  };
  const watchIsForeign = watch("isForeign");
  // const watchIsForeign = watch();
  const filteredCountries = Countries.filter(
    (country: Country) =>
      !stringToBoolean(watchIsForeign) || country.code !== "MOZ"
  );

  const handleEmailChange = (e: any) => {
    var email = e.target.value;
    setValue("email", email);
    // setValue("userName", email);
  };
  const handleSecondaryEmailChange = (e: any) => {
    var secondaryEmail = e.target.value;
    setValue("secondaryEmail", secondaryEmail);
    // setValue("userName", email);
  };
  const fetchUserNameSuggestion = async (fullName: string) => {
    if (!fullName) return;
    //  setLoadingUserName(true);
    try {
      const response = await getUserNameSuggestion({ name: fullName });
      setValue("userName", response);
      //   setUserNameError(null);
    } catch (error: any) {
      // setUserNameError(error.message || 'Erro ao buscar sugestão');
    } finally {
      //setLoadingUserName(false);
    }
  };

  const debouncedFetchUserName = useCallback(
    debounce(fetchUserNameSuggestion, 500),
    []
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setValue("name", name);
    !isUpdateRoute && debouncedFetchUserName(name);
  };

  const handleRoleChange = (roleId: string) => {
    setValue("roleId", roleId);
  };
  const handleOnChangeIsForeign = (e: any) => {
    const isIsForeign = stringToBoolean(getValues(e.target.value));
    if (isIsForeign) {
      setValue("countryDescription", "");
    }
    if (!isIsForeign) {
      setValue("countryDescription", "MOZ");
    }
  };
  return (
    <Card size="3">
      <form onSubmit={onSubmit} noValidate>
        <Flex direction="column" gap="4">
          <Flex gap="8">
            <TextFieldComponent
              name="name"
              label="Nome"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "Nome completo",
                onChange: handleNameChange,
              }}
              style={{ width: "100%" }}
              required={true}
            />
            <TextFieldComponent
              name="userName"
              label="Utilizador"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "Utilizador",
                disabled: "disabled",
              }}
              style={{ width: "100%" }}
              required={true}
            />
            <TextFieldComponent
              name="email"
              label="Email"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "seu@email.com",
                onChange: handleEmailChange,
                type: "email",
              }}
              style={{ width: "100%" }}
              required={true}
            />
            <TextFieldComponent
              name="secondaryEmail"
              label="Email alternativo"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "seu@email.com",
                onChange: handleSecondaryEmailChange,
                type: "email",
              }}
              style={{ width: "100%" }}
            />
          </Flex>
          <Flex gap="8">
            <TextFieldComponent
              name="bi"
              label="BI"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "BI" }}
              style={{ width: "100%" }}
              required={true}
            />
            <TextFieldComponent
              name="nuit"
              label="NUIT"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Seu NUIT" }}
              style={{ width: "100%" }}
              required={true}
            />
            <TextFieldComponent
              name="phoneNumber"
              label="Telefone"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "Ex: 82xxxxxxx"
              }}
              style={{ width: "100%" }}
              required={true}
            />

            <TextFieldComponent
              name="secondaryPhoneNumber"
              label="Telefone alternativo"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "82xxxxxxx"
              }}
              style={{ width: "100%" }}
            />
          </Flex>
          <Flex gap="8">
            <DateFieldComponent
              name="dateOfBirth"
              label="Data de Nascimento"
              control={control}
              errors={errors}
              style={{ width: "100%" }}
              required={true}
            />
            <RadioGroupComponent
              name="gender"
              label="Gênero"
              control={control}
              errors={errors}
              style={{ width: "100%" }}
              options={[
                { value: "M", label: "Masculino" },
                { value: "F", label: "Feminino" },
              ]}
              fieldProps={{
                style: { display: "flex", flexDirection: "row", gap: "16px" },
              }}
            />
            {/* <SelectComponent
              name="roleId"
              label="Perfil"
              control={control}
              errors={errors}
              style={{ width: "100%" }}
              options={roles?.items.map((role) => ({
                value: role.name ?? "",
                label: role.name ?? "",
              }))}
              onSelect={handleRoleChange}
              itemsError={isErrorRoles}
              itemsLoading={isLoadingRoles}
            /> */}
            {defaultValues.id && isUpdateRoute && (
              <RadioGroupComponent
                name="twoFactorEnabled"
                label="Autenticação de 2 fatores?"
                control={control}
                errors={errors}
                style={{ width: "100%" }}
                options={[
                  { value: "true", label: "Sim" },
                  { value: "false", label: "Não" },
                ]}
                fieldProps={{
                  style: { display: "flex", flexDirection: "row", gap: "16px" },
                }}
              />
            )}
          </Flex>
          <Flex gap="6">
            <MultiSelectComponent
              name="roleIds"
              label="Perfis"
              control={control}
              errors={errors}
              style={{ width: "100%" }}
              options={roles?.items.map((role) => ({
                value: role.name ?? "",
                label: role.name ?? "",
              }))}
              itemsError={isErrorRoles}
              itemsLoading={isLoadingRoles}
            />
          </Flex>
          <Flex gap="6">
            <RadioGroupComponent
              name="isForeign"
              label="Estrangeiro"
              control={control}
              errors={errors}
              style={{ width: "50%" }}
              options={[
                { value: "true", label: "Sim" },
                { value: "false", label: "Não" },
              ]}
              fieldProps={{
                style: { display: "flex", flexDirection: "row", gap: "16px" },
                onChange: handleOnChangeIsForeign,
              }}
            />
            <SelectSearchableComponent
              name="countryDescription"
              label="País"
              control={control}
              errors={errors}
              options={filteredCountries?.map((country: Country) => ({
                value: country.code,
                label: country.label ?? "",
              }))}
              style={{ width: "100%" }}
              fieldProps={{ disabled: !stringToBoolean(watchIsForeign) }}
            />
          </Flex>

          <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
            <Button
              color="red"
              onClick={handleBack}
              disabled={mutation.isLoading}
            >
              <ChevronLeftIcon /> voltar
            </Button>

            <Button
              type="submit"
              disabled={mutation.isLoading}
              variant="surface"
            >
              {mutation.isLoading ? (
                <Spinner loading>
                  {" "}
                  <EnterIcon />
                </Spinner>
              ) : (
                <EnterIcon />
              )}
              {defaultValues.id && isUpdateRoute ? "Atualizar" : "Registar"}
            </Button>
          </Flex>
        </Flex>
      </form>
      {showChangesNotification && (
        <AlertDialog.Root open={showChangesNotification} onOpenChange={() => {
          setShowChangesNotification(false)
          handleBack();
        }}>
          <AlertDialog.Content maxWidth="1000px">
            <AlertDialog.Title>Alterações salvas</AlertDialog.Title>
            <AlertDialog.Description>
              As alterações feitas terão efeito após o próximo login do utilizador.
            </AlertDialog.Description>
            <Flex gap="3" justify="center" mt="4">
              <AlertDialog.Cancel>
                <Button variant="soft" color="orange">
                  Entendido
                </Button>
              </AlertDialog.Cancel>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      )}
    </Card>
  );
};
